import { Fragment, useState, type FormEvent } from 'react';
import { endpoints, type Expedition, type Killmail } from '../api';
import { Button, EmptyState, ErrorState, Field, LoadingState, Note, Page, Section, currency, dateTime, number } from '../components';
import { useApiQuery } from '../hooks';

const outcomeLabels: Record<Killmail['outcome'], string> = { loss: 'Pérdida', kill: 'Baja', reference: 'Referencia' };

function compartment(flag: number) {
  if (flag === 5) return 'Carga';
  if (flag >= 27 && flag <= 34) return 'Ranura alta';
  if (flag >= 19 && flag <= 26) return 'Ranura media';
  if (flag >= 11 && flag <= 18) return 'Ranura baja';
  if (flag >= 92 && flag <= 99) return 'Rig';
  if (flag === 87) return 'Bahía de drones';
  return 'Otro compartimento';
}

function TrackingForm({ mail, expeditions, onSaved }: { mail: Killmail; expeditions: Expedition[]; onSaved: () => Promise<void> }) {
  const [expeditionId, setExpeditionId] = useState(mail.expeditionId ?? '');
  const [cost, setCost] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const booked = mail.financeRecordId != null || mail.actualCost != null;

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amount = Number(cost);
    if (cost && (!Number.isFinite(amount) || amount <= 0)) {
      setMessage({ tone: 'error', text: 'Introduce un coste real mayor que cero.' });
      return;
    }
    setBusy(true); setMessage(null);
    try {
      await endpoints.updateKillmailTracking(mail.id, { expeditionId: expeditionId || null, ...(!booked && mail.outcome === 'loss' && cost ? { actualCost: amount } : {}) });
      setCost('');
      await onSaved();
      setMessage({ tone: 'success', text: 'Seguimiento guardado.' });
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : 'No se pudo guardar el seguimiento.' });
    } finally { setBusy(false); }
  }

  return <form className="form-grid" onSubmit={(event) => void save(event)}>
    <Field label="Expedición"><select value={expeditionId} onChange={(event) => setExpeditionId(event.target.value)}><option value="">Sin expedición</option>{expeditions.map((expedition) => <option key={expedition.id} value={expedition.id}>{expedition.name} · {expedition.systemName}</option>)}</select></Field>
    {booked ? <div className="fine">Coste real: {mail.actualCost != null ? currency(mail.actualCost) : 'registrado'}. <a className="link" href="#/finance-records">Costes y lotes</a></div> : mail.outcome === 'loss' && <Field label="Coste real confirmado (ISK)" hint="Opcional. Una vez registrado no se puede cambiar; no duplica el flujo de ESI."><input type="number" min="0.01" step="any" value={cost} onChange={(event) => setCost(event.target.value)} /></Field>}
    <div className="span actions"><Button variant="secondary" disabled={busy}>{busy ? 'Guardando…' : 'Guardar seguimiento'}</Button></div>
    {message && <div className="span"><Note tone={message.tone}>{message.text}</Note></div>}
  </form>;
}

export function KillmailsPage() {
  const query = useApiQuery(endpoints.killmails);
  const expeditions = useApiQuery(endpoints.expeditions);
  const [reference, setReference] = useState('');
  const [filter, setFilter] = useState<'all' | Killmail['outcome']>('all');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  async function importKillmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = reference.trim();
    if (!value || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const imported = await endpoints.importKillmail(value);
      setReference('');
      setFilter('all');
      setMessage({ tone: 'success', text: `Killmail ${imported.id} importado correctamente.` });
      await query.reload();
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : 'No se pudo importar el killmail.' });
    } finally {
      setBusy(false);
    }
  }

  const visible = query.data?.filter((mail) => filter === 'all' || mail.outcome === filter);
  return <Page title="Bajas y pérdidas" actions={<Button variant="ghost" disabled={query.loading} onClick={() => void query.reload()}>Actualizar</Button>}>
    <Section>
      <form className="filters" onSubmit={(event) => void importKillmail(event)}>
        <Field label="Importar desde zKillboard" wide><input required value={reference} onChange={(event) => setReference(event.target.value)} placeholder="URL o ID del killmail" /></Field>
        <Button variant="secondary" disabled={busy || !reference.trim()}>{busy ? 'Importando…' : 'Importar'}</Button>
        <Field label="Mostrar"><select value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}><option value="all">Todos</option><option value="loss">Pérdidas</option><option value="kill">Bajas</option><option value="reference">Referencias</option></select></Field>
      </form>
      {message && <Note tone={message.tone}>{message.text}</Note>}
      {expeditions.error && <ErrorState error={expeditions.error} onRetry={() => void expeditions.reload()} />}
      {query.loading && !query.data && <LoadingState />}
      {query.error && <ErrorState error={query.error} onRetry={() => void query.reload()} />}
      {!query.loading && visible && visible.length === 0 && <EmptyState title="Sin killmails" />}
      {visible && visible.length > 0 && <div className="table-wrap"><table className="table--stack"><thead><tr><th aria-label="Detalle" /><th>Nave</th><th>Resultado</th><th>Víctima</th><th>Sistema</th><th>Fecha</th><th className="num">Valor est.</th><th className="num">Botín est.</th></tr></thead><tbody>{visible.map((mail) => {
        const open = expanded === mail.id;
        return <Fragment key={mail.id}><tr className={open ? 'is-open' : undefined}>
          <td style={{ width: 28, paddingRight: 4 }}><button type="button" className="expand" aria-expanded={open} aria-label={`Detalle del killmail ${mail.id}`} onClick={() => setExpanded(open ? null : mail.id)}>▶</button></td>
          <td className="wide"><strong>{mail.shipName}</strong></td>
          <td data-label="Resultado" className={mail.outcome === 'loss' ? 'neg' : mail.outcome === 'kill' ? 'pos' : 'muted'}>{outcomeLabels[mail.outcome]}</td>
          <td data-label="Víctima">{mail.victimName}</td>
          <td data-label="Sistema">{mail.systemName}</td>
          <td className="muted" data-label="Fecha" style={{ whiteSpace: 'nowrap' }}>{dateTime(mail.occurredAt)}</td>
          <td className="num" data-label="Valor est.">{currency(mail.totalValue)}</td>
          <td className="num" data-label="Botín est.">{currency(mail.droppedValue)}</td>
        </tr>{open && <tr className="detail-row"><td colSpan={8}><div className="detail">
          <dl className="meta">
            <div><dt>Killmail</dt><dd><a className="link" href={mail.url} target="_blank" rel="noopener noreferrer">#{mail.id} ↗</a></dd></div>
            <div><dt>Daño recibido</dt><dd>{number(mail.damageTaken)}{mail.npc ? ' · NPC' : ''}{mail.solo ? ' · Solo' : ''}</dd></div>
            <div><dt>Destruido est.</dt><dd>{currency(mail.destroyedValue)}</dd></div>
          </dl>
          <details><summary>Atacantes ({mail.attackers.length})</summary>{mail.attackers.length === 0 ? <p className="muted">Sin registrar.</p> : <table><thead><tr><th>Atacante</th><th>Nave</th><th className="num">Daño</th><th>Golpe final</th></tr></thead><tbody>{mail.attackers.map((attacker, index) => <tr key={index}><td>{attacker.name}</td><td>{attacker.shipName}</td><td className="num">{number(attacker.damage)}</td><td>{attacker.finalBlow ? 'Sí' : ''}</td></tr>)}</tbody></table>}</details>
          <details><summary>Objetos ({mail.items.length})</summary>{mail.items.length === 0 ? <p className="muted">Sin registrar.</p> : <table><thead><tr><th>Objeto</th><th className="num">Destruidos</th><th className="num">Botín</th><th>Ubicación</th></tr></thead><tbody>{mail.items.map((item, index) => <tr key={index}><td>{item.name}</td><td className="num">{number(item.destroyed)}</td><td className="num">{number(item.dropped)}</td><td>{compartment(item.flag)}</td></tr>)}</tbody></table>}</details>
          {expeditions.data && <TrackingForm key={`${mail.id}-${mail.expeditionId ?? ''}-${mail.financeRecordId ?? ''}`} mail={mail} expeditions={expeditions.data} onSaved={query.reload} />}
        </div></td></tr>}</Fragment>;
      })}</tbody></table></div>}
      <p className="fine">Los valores de zKillboard son estimaciones, no ingresos ni gastos contabilizados.</p>
    </Section>
  </Page>;
}
